// ============================================
// Conversation View — Fashion Plate chat interface
// Patient talks to the dressed nurse; clinical
// capture pills accumulate in the CaptureStrip.
// ============================================

import { useState, useCallback, useRef, useEffect } from 'react';
import { T, F } from '../../quality/tokens';
import { GarmentBar } from './GarmentBar';
import { CaptureStrip } from './CaptureStrip';
import type { GarmentState, GarmentPiece, CapturedSignal, CaptureConfig } from '../../../types/fashionPlate';
import type { ContextLayer } from '../../../types/patientJourney';
import { geminiChat, type GeminiMessage } from '../../../services/ai/providers/gemini';
import { extractSignals, buildCaptureReinforcement } from '../../../services/fashionPlate/captureExtractor';
import { buildFoundation, buildVertical } from '../../../services/patientJourney/promptLayers';

const MODEL = 'gemini-2.5-flash';

interface ConversationViewProps {
  garments: GarmentState[];
  personaName: string;
  contextSnapshot: Partial<ContextLayer>;
  captureConfig: CaptureConfig;
  onBack: () => void;
  onGarmentTap: (piece: GarmentPiece) => void;
}

interface DisplayMessage {
  role: 'nurse' | 'patient';
  text: string;
}

function buildSystemPrompt(
  personaName: string,
  garments: GarmentState[],
  contextSnapshot: Partial<ContextLayer>,
  captureReinforcement: string,
): string {
  const garmentList = garments
    .map((g) => `${g.piece}: ${g.label} (${g.intensity})`)
    .join(', ');

  // Foundation layer — locked rules (HRT echo trap, boundaries, honesty)
  const foundation = buildFoundation();

  // Vertical layer — voice rules, clinical rules (no biomarker checklist or formulary in test mode)
  const vertical = buildVertical([], '', 1);

  return `You are an AI nurse in the HemoLogix health platform. Your persona is "${personaName}".

Your current configuration:
- Garments: ${garmentList}
- Context: ${JSON.stringify(contextSnapshot)}

${foundation}

${vertical}

===== ADDITIONAL RULES =====
- Reference any captured clinical signals naturally in your responses.
- If you notice health-relevant information, acknowledge it.

${captureReinforcement}`;
}

export function ConversationView({
  garments,
  personaName,
  contextSnapshot,
  captureConfig,
  onBack,
  onGarmentTap: _onGarmentTap,
}: ConversationViewProps) {
  const [chatHistory, setChatHistory] = useState<GeminiMessage[]>([]);
  const [displayMessages, setDisplayMessages] = useState<DisplayMessage[]>([]);
  const [capturedSignals, setCapturedSignals] = useState<CapturedSignal[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [turnNumber, setTurnNumber] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasInitialized = useRef(false);

  // Auto-scroll to bottom on new messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [displayMessages, scrollToBottom]);

  // Opening message on mount
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    async function sendOpening() {
      setIsLoading(true);
      try {
        const systemPrompt = buildSystemPrompt(personaName, garments, contextSnapshot, '');
        const openingText = await geminiChat(
          MODEL,
          systemPrompt,
          [],
          'The patient has just arrived. Introduce yourself warmly and ask how they are feeling today.',
          0.7,
          256,
        );

        const nurseMessage: GeminiMessage = {
          role: 'model',
          parts: [{ text: openingText }],
        };

        setChatHistory([
          { role: 'user', parts: [{ text: 'The patient has just arrived. Introduce yourself warmly and ask how they are feeling today.' }] },
          nurseMessage,
        ]);
        setDisplayMessages([{ role: 'nurse', text: openingText }]);
      } catch {
        setDisplayMessages([{
          role: 'nurse',
          text: `Hi there, I'm your nurse for today. How are you feeling?`,
        }]);
      } finally {
        setIsLoading(false);
      }
    }

    sendOpening();
  }, [personaName, garments, contextSnapshot]);

  // Send patient message
  const handleSend = useCallback(async () => {
    const message = inputText.trim();
    if (!message || isLoading) return;

    const currentTurn = turnNumber + 1;
    setTurnNumber(currentTurn);
    setInputText('');
    setIsLoading(true);

    // Append patient message to display immediately
    setDisplayMessages((prev) => [...prev, { role: 'patient', text: message }]);

    // Build updated chat history with user message
    const userGeminiMsg: GeminiMessage = { role: 'user', parts: [{ text: message }] };
    const updatedHistory = [...chatHistory, userGeminiMsg];

    try {
      // Run signal extraction and nurse response in parallel
      const [newSignals, nurseResponse] = await Promise.all([
        extractSignals(message, currentTurn, capturedSignals),
        (async () => {
          // Build reinforcement with current signals (nurse may not see brand-new ones until next turn)
          const reinforcement = buildCaptureReinforcement(capturedSignals, captureConfig, currentTurn);
          const systemPrompt = buildSystemPrompt(personaName, garments, contextSnapshot, reinforcement);
          return geminiChat(MODEL, systemPrompt, updatedHistory, message, 0.7, 512);
        })(),
      ]);

      // Append nurse response to display
      setDisplayMessages((prev) => [...prev, { role: 'nurse', text: nurseResponse }]);

      // Update chat history with both user and model messages
      const modelGeminiMsg: GeminiMessage = { role: 'model', parts: [{ text: nurseResponse }] };
      setChatHistory([...updatedHistory, modelGeminiMsg]);

      // Append new captured signals
      if (newSignals.length > 0) {
        setCapturedSignals((prev) => [...prev, ...newSignals]);
      }
    } catch {
      setDisplayMessages((prev) => [
        ...prev,
        { role: 'nurse', text: 'I apologize, I had a brief interruption. Could you repeat that for me?' },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [inputText, isLoading, turnNumber, chatHistory, capturedSignals, captureConfig, personaName, garments, contextSnapshot]);

  // Handle Enter key (send on Enter, newline on Shift+Enter)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Auto-resize textarea
  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 100)}px`;
  }, []);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: T.bg,
    }}>
      {/* Back button */}
      <button
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: T.fontSize.sm,
          color: T.textMuted,
          fontFamily: F.body,
          padding: '8px 16px',
          flexShrink: 0,
        }}
      >
        &#8592; Back to Dressing Room
      </button>

      {/* Capture Strip */}
      <CaptureStrip signals={capturedSignals} />

      {/* Compact Garment Bar */}
      <div style={{ padding: '8px 16px 0', flexShrink: 0 }}>
        <GarmentBar garments={garments} compact />
      </div>

      {/* Chat message area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}>
        {displayMessages.map((msg, i) => {
          const isNurse = msg.role === 'nurse';
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isNurse ? 'flex-start' : 'flex-end',
              }}
            >
              {/* Label */}
              <span style={{
                fontSize: 9,
                fontFamily: F.mono,
                fontWeight: 600,
                color: isNurse ? '#8B5CF6' : T.textMuted,
                marginBottom: 3,
                letterSpacing: 0.5,
              }}>
                {isNurse ? 'Nurse' : 'You'}
              </span>

              {/* Bubble */}
              <div style={{
                maxWidth: '85%',
                padding: '10px 14px',
                fontSize: T.fontSize.sm,
                fontFamily: F.body,
                lineHeight: 1.5,
                color: T.text,
                background: isNurse ? T.bgCard : '#8B5CF610',
                border: isNurse ? `1px solid ${T.border}` : '1px solid #8B5CF620',
                borderRadius: isNurse
                  ? `2px ${T.radius.lg}px ${T.radius.lg}px ${T.radius.lg}px`
                  : `${T.radius.lg}px 2px ${T.radius.lg}px ${T.radius.lg}px`,
                boxShadow: isNurse ? T.shadowSm : 'none',
                wordBreak: 'break-word',
              }}>
                {msg.text}
              </div>
            </div>
          );
        })}

        {/* Loading indicator */}
        {isLoading && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}>
            <span style={{
              fontSize: 9,
              fontFamily: F.mono,
              fontWeight: 600,
              color: '#8B5CF6',
              marginBottom: 3,
              letterSpacing: 0.5,
            }}>
              Nurse
            </span>
            <div style={{
              padding: '10px 14px',
              background: T.bgCard,
              border: `1px solid ${T.border}`,
              borderRadius: `2px ${T.radius.lg}px ${T.radius.lg}px ${T.radius.lg}px`,
              boxShadow: T.shadowSm,
            }}>
              <span style={{
                fontSize: T.fontSize.sm,
                fontFamily: F.body,
                color: T.textMuted,
              }}>
                &#8230;
              </span>
            </div>
          </div>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Input bar */}
      <div style={{
        display: 'flex',
        gap: 8,
        padding: '12px 16px',
        borderTop: `1px solid ${T.border}`,
        background: T.bgCard,
        flexShrink: 0,
      }}>
        <textarea
          ref={textareaRef}
          value={inputText}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          placeholder="Tell the nurse how you're feeling..."
          rows={1}
          style={{
            flex: 1,
            minHeight: 40,
            maxHeight: 100,
            padding: '8px 12px',
            resize: 'none',
            background: T.bgInput,
            border: `1px solid ${T.borderSoft}`,
            borderRadius: T.radius.md,
            fontFamily: F.body,
            fontSize: T.fontSize.sm,
            color: T.text,
            outline: 'none',
            lineHeight: 1.4,
          }}
        />
        <button
          onClick={handleSend}
          disabled={isLoading || !inputText.trim()}
          style={{
            width: 40,
            height: 40,
            borderRadius: T.radius.md,
            background: isLoading || !inputText.trim() ? T.bgInput : '#8B5CF6',
            color: isLoading || !inputText.trim() ? T.textGhost : '#fff',
            border: 'none',
            cursor: isLoading || !inputText.trim() ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            fontWeight: 700,
            flexShrink: 0,
            transition: 'background 0.15s ease, color 0.15s ease',
          }}
        >
          &#8594;
        </button>
      </div>
    </div>
  );
}
